import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/spinner/spinner.js';
import '@awesome.me/webawesome/dist/components/tag/tag.js';
import '@awesome.me/webawesome/dist/components/textarea/textarea.js';

import { ApiError, api, formValues, invoiceStatusLabels, type BankAccount, type InvoiceDetail, type InvoiceLine, type InvoiceSection } from '../api.js';
import { navigate } from '../router.js';
import { pageStyles } from '../shared-styles.js';
import '../components/audit-dialog.js';
import type { AuditDialog } from '../components/audit-dialog.js';

/** One invoice: header, sections and lines, preview, lifecycle actions. */
@customElement('invoice-page')
export class InvoicePage extends LitElement {
  static styles = [
    pageStyles,
    css`
      .layout {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 1.5rem;
        align-items: start;
      }
      @media (max-width: 1100px) {
        .layout {
          grid-template-columns: 1fr;
        }
      }
      .header-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.5rem 1rem;
        margin-bottom: 1rem;
      }
      .header-grid .full {
        grid-column: 1 / -1;
      }
      .kv {
        display: grid;
        grid-template-columns: max-content 1fr;
        gap: 0.2rem 1rem;
        margin-bottom: 1rem;
      }
      .kv dt {
        color: var(--wa-color-neutral-60);
      }
      .kv dd {
        margin: 0;
      }
      .lines td.section {
        font-weight: 600;
        padding-top: 0.8rem;
      }
      .lines tr.subtotal td {
        font-weight: 600;
        border-top: 1px solid var(--wa-color-neutral-80);
      }
      .lines td.kind {
        color: var(--wa-color-neutral-60);
        font-size: 0.8em;
      }
      .totals {
        margin-left: auto;
        width: max-content;
        min-width: 18rem;
      }
      .totals .grand td {
        font-weight: 700;
        border-top: 2px solid var(--wa-color-neutral-70);
      }
      iframe {
        width: 100%;
        height: 80rem;
        border: 1px solid var(--wa-color-neutral-80);
        background: #fff;
      }
      .actions-bar {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
      }
    `,
  ];

  @property({ type: Number }) invoiceId = 0;

  @state() private detail: InvoiceDetail | null = null;
  @state() private accounts: BankAccount[] = [];
  @state() private loading = true;
  @state() private busy = false;
  @state() private error = '';
  @state() private editingHeader = false;
  @state() private previewKey = 0;

  @state() private lineDialogOpen = false;
  @state() private lineEditing: InvoiceLine | null = null;
  @state() private lineError = '';

  connectedCallback() {
    super.connectedCallback();
    void this.init();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('invoiceId') && changed.get('invoiceId') !== undefined) void this.load();
  }

  private async init() {
    try {
      this.accounts = await api.get<BankAccount[]>('/api/bank-accounts');
    } catch (e) {
      this.error = (e as Error).message;
    }
    await this.load();
  }

  private async load() {
    this.loading = true;
    this.error = '';
    try {
      this.detail = await api.get<InvoiceDetail>(`/api/invoices/${this.invoiceId}`);
      this.previewKey++;
    } catch (e) {
      this.error = (e as Error).message;
      this.detail = null;
    } finally {
      this.loading = false;
    }
  }

  /** Runs a call, shows its error, and refreshes from its response. */
  private async act(fn: () => Promise<InvoiceDetail | void>) {
    this.busy = true;
    this.error = '';
    try {
      const d = await fn();
      if (d) {
        this.detail = d;
        this.previewKey++;
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const hint = Object.values(err.fields)[0];
        this.error = hint ? `${err.message}: ${hint}` : err.message;
      } else {
        this.error = (err as Error).message;
      }
    } finally {
      this.busy = false;
    }
  }

  // ---- header ----

  private async saveHeader(e: Event) {
    e.preventDefault();
    const v = formValues(e.target as HTMLFormElement);
    await this.act(async () => {
      const d = await api.put<InvoiceDetail>(`/api/invoices/${this.invoiceId}`, {
        invoice_date: v.invoice_date,
        due_date: v.due_date,
        bank_account_id: v.bank_account_id ? Number(v.bank_account_id) : null,
        reference: v.reference,
        note: v.note,
        vat_rate: v.vat_rate ?? '0',
      });
      this.editingHeader = false;
      return d;
    });
  }

  // ---- lines ----

  private openLine(line: InvoiceLine | null) {
    this.lineEditing = line;
    this.lineError = '';
    this.lineDialogOpen = true;
  }

  private async saveLine(e: Event) {
    e.preventDefault();
    const v = formValues(e.target as HTMLFormElement);
    this.busy = true;
    this.lineError = '';
    try {
      const body = { description: v.description, units: v.units, unit_price: v.unit_price };
      let d: InvoiceDetail;
      if (this.lineEditing) {
        d = await api.put<InvoiceDetail>(`/api/invoices/${this.invoiceId}/lines/${this.lineEditing.id}`, body);
      } else {
        const section = v.section_id ?? '';
        d = await api.post<InvoiceDetail>(`/api/invoices/${this.invoiceId}/lines`, section === '__new__' ? { ...body, section_title: v.section_title } : { ...body, section_id: Number(section) });
      }
      this.detail = d;
      this.previewKey++;
      this.lineDialogOpen = false;
    } catch (err) {
      if (err instanceof ApiError) {
        const hint = Object.values(err.fields)[0];
        this.lineError = hint ? `${err.message}: ${hint}` : err.message;
      } else {
        this.lineError = (err as Error).message;
      }
    } finally {
      this.busy = false;
    }
  }

  private async removeLine(line: InvoiceLine) {
    if (!confirm(`Remove the line "${line.description}"?`)) return;
    await this.act(async () => {
      await api.delete(`/api/invoices/${this.invoiceId}/lines/${line.id}`);
      await this.load();
    });
  }

  // ---- lifecycle ----

  private recalculate() {
    return this.act(() => api.post<InvoiceDetail>(`/api/invoices/${this.invoiceId}/recalculate`, {}));
  }

  private issue() {
    const d = this.detail;
    if (!d) return;
    if (!confirm(`Issue this invoice to ${d.client_name} for ${d.total} ${d.currency}?\n\nThe next number of ${d.invoice_date.slice(0, 4)} is assigned, the time entries of ${d.period_from.slice(0, 7)} are marked as invoiced and the PDF is filed under Documents. Lines can no longer be edited.`)) return;
    return this.act(() => api.post<InvoiceDetail>(`/api/invoices/${this.invoiceId}/issue`, {}));
  }

  private regeneratePdf() {
    return this.act(() => api.post<InvoiceDetail>(`/api/invoices/${this.invoiceId}/pdf`, {}));
  }

  private archive(un: boolean) {
    return this.act(() => api.post<InvoiceDetail>(`/api/invoices/${this.invoiceId}/${un ? 'unarchive' : 'archive'}`, {}));
  }

  private async deleteInvoice() {
    const d = this.detail;
    if (!d) return;
    const msg =
      d.status === 'draft'
        ? 'Delete this draft?'
        : `Delete issued invoice ${d.number}?\n\nIts time entries become billable again and the PDF document is removed. The number ${d.number} stays used (a gap in the sequence).`;
    if (!confirm(msg)) return;
    await this.act(async () => {
      await api.delete(`/api/invoices/${this.invoiceId}`);
      navigate('/invoices');
    });
  }

  private history() {
    const d = this.detail;
    if (!d) return;
    void (this.renderRoot.querySelector('audit-dialog') as AuditDialog).show('invoice', d.id, d.number ?? `draft #${d.id}`);
  }

  private onDialogHidden = (e: Event) => {
    if (e.target === e.currentTarget) this.lineDialogOpen = false;
  };

  // ---- render ----

  render() {
    const d = this.detail;
    return html`
      <div class="page-header">
        <wa-button size="small" appearance="plain" @click=${() => navigate('/invoices')}>← Invoices</wa-button>
        <h2>${d ? (d.number ? `Invoice ${d.number}` : 'Invoice draft') : 'Invoice'}</h2>
        ${d ? html`<wa-tag variant=${d.status === 'draft' ? 'warning' : d.status === 'issued' ? 'success' : 'neutral'}>${invoiceStatusLabels[d.status]}</wa-tag>` : nothing}
        <span class="spacer"></span>
        ${d ? this.renderActions(d) : nothing}
      </div>
      ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
      ${this.loading && !d ? html`<wa-spinner></wa-spinner>` : nothing}
      ${d ? this.renderBody(d) : nothing}
      ${this.renderLineDialog()}
      <audit-dialog></audit-dialog>
    `;
  }

  private renderActions(d: InvoiceDetail) {
    const draft = d.status === 'draft';
    return html`
      <div class="actions-bar">
        <wa-button size="small" appearance="plain" @click=${this.history}>History</wa-button>
        <wa-button size="small" appearance="outlined" href=${`/api/invoices/${d.id}/pdf`} target="_blank" ?disabled=${!draft && !d.document_id}>Download PDF</wa-button>
        ${draft
          ? html`
              <wa-button size="small" appearance="outlined" ?loading=${this.busy} @click=${this.recalculate}>Recalculate</wa-button>
              <wa-button size="small" variant="brand" ?loading=${this.busy} ?disabled=${d.stale || d.sections.length === 0 || !d.bank_account_id} @click=${this.issue}>Issue</wa-button>
            `
          : html`
              <wa-button size="small" appearance="outlined" ?loading=${this.busy} @click=${this.regeneratePdf}>Regenerate PDF</wa-button>
              ${d.status === 'issued'
                ? html`<wa-button size="small" appearance="outlined" @click=${() => this.archive(false)}>Archive</wa-button>`
                : html`<wa-button size="small" appearance="outlined" @click=${() => this.archive(true)}>Unarchive</wa-button>`}
            `}
        <wa-button size="small" appearance="plain" variant="danger" ?disabled=${d.ujp_status === 'registered'} @click=${this.deleteInvoice}>Delete</wa-button>
      </div>
    `;
  }

  private renderBody(d: InvoiceDetail) {
    return html`
      ${d.stale
        ? html`<wa-callout variant="warning"><strong>The timesheets changed</strong> since this draft was generated. Recalculate before issuing; manual lines are kept.</wa-callout>`
        : nothing}
      ${d.warnings.length
        ? html`<wa-callout variant=${d.warnings.some((w) => w.kind === 'pdf_failed') ? 'danger' : 'warning'}>
            <div class="check-lines">${d.warnings.map((w) => html`<div>${w.message}</div>`)}</div>
          </wa-callout>`
        : nothing}
      <div class="layout">
        <div>
          ${this.editingHeader ? this.renderHeaderForm(d) : this.renderHeader(d)}
          ${this.renderLines(d)}
        </div>
        <div>
          <iframe title="Invoice preview" src=${`/api/invoices/${d.id}/preview?v=${this.previewKey}`} sandbox=""></iframe>
        </div>
      </div>
    `;
  }

  private renderHeader(d: InvoiceDetail) {
    return html`
      <dl class="kv">
        <dt>Client</dt>
        <dd>${d.client_name}</dd>
        <dt>Period</dt>
        <dd>${d.period_from} – ${d.period_to}</dd>
        <dt>Invoice date</dt>
        <dd>${d.invoice_date}</dd>
        <dt>Due date</dt>
        <dd>${d.due_date}</dd>
        <dt>Bank account</dt>
        <dd>${d.bank_account_label ?? html`<span class="warn">none for ${d.currency}</span>`}</dd>
        <dt>Reference</dt>
        <dd>${d.reference ?? html`<span class="muted">—</span>`}</dd>
        <dt>VAT</dt>
        <dd>${d.vat_rate} %</dd>
        <dt>Note</dt>
        <dd>${d.note ?? html`<span class="muted">—</span>`}</dd>
        ${d.issued_at
          ? html`<dt>Issued</dt>
              <dd>${d.issued_at.slice(0, 10)} by ${d.issued_by}</dd>`
          : nothing}
        ${d.document_id
          ? html`<dt>Document</dt>
              <dd><a href="/api/documents/${d.document_id}/download" target="_blank" rel="noopener">PDF</a> (also under Documents)</dd>`
          : nothing}
      </dl>
      ${d.status === 'draft' ? html`<wa-button size="small" appearance="outlined" @click=${() => (this.editingHeader = true)}>Edit header</wa-button>` : nothing}
    `;
  }

  private renderHeaderForm(d: InvoiceDetail) {
    const accounts = this.accounts.filter((a) => a.currency === d.currency && (a.is_active || a.id === d.bank_account_id));
    return html`
      <form class="header-grid" @submit=${this.saveHeader}>
        <wa-input name="invoice_date" label="Invoice date" type="date" required .value=${d.invoice_date}></wa-input>
        <wa-input name="due_date" label="Due date" type="date" required .value=${d.due_date}></wa-input>
        <wa-select name="bank_account_id" label=${`Bank account (${d.currency})`} .value=${d.bank_account_id ? String(d.bank_account_id) : ''}>
          <wa-option value="">— none —</wa-option>
          ${accounts.map((a) => html`<wa-option value=${String(a.id)}>${a.label}${a.is_default ? ' (default)' : ''}</wa-option>`)}
        </wa-select>
        <wa-input name="vat_rate" label="VAT %" inputmode="decimal" required .value=${d.vat_rate}></wa-input>
        <wa-input class="full" name="reference" label="Reference" .value=${d.reference ?? ''}></wa-input>
        <wa-textarea class="full" name="note" label="Note (printed at the bottom)" rows="3" .value=${d.note ?? ''}></wa-textarea>
        <div class="full footer">
          <wa-button appearance="outlined" @click=${() => (this.editingHeader = false)}>Cancel</wa-button>
          <wa-button variant="brand" type="submit" ?loading=${this.busy}>Save header</wa-button>
        </div>
      </form>
    `;
  }

  private renderLines(d: InvoiceDetail) {
    const draft = d.status === 'draft';
    return html`
      <div class="table-wrap"><table class="entries lines">
        <thead>
          <tr>
            <th>Description</th>
            <th class="num">Units</th>
            <th class="num">Unit price</th>
            <th class="num">Amount</th>
            ${draft ? html`<th></th>` : nothing}
          </tr>
        </thead>
        <tbody>
          ${d.sections.length === 0 ? html`<tr><td colspan="5" class="muted">No lines. Import the month's timesheets or add a manual line.</td></tr>` : nothing}
          ${d.sections.map((s) => this.renderSection(s, draft))}
        </tbody>
      </table></div>
      ${draft ? html`<wa-button size="small" appearance="outlined" @click=${() => this.openLine(null)}>Add line</wa-button>` : nothing}
      <div class="table-wrap"><table class="totals">
        <tr><td>Subtotal</td><td class="num">${d.subtotal} ${d.currency}</td></tr>
        <tr><td>VAT ${d.vat_rate} %</td><td class="num">${d.vat_amount} ${d.currency}</td></tr>
        <tr class="grand"><td>Total</td><td class="num">${d.total} ${d.currency}</td></tr>
      </table></div>
    `;
  }

  private renderSection(s: InvoiceSection, draft: boolean) {
    return html`
      <tr><td colspan=${draft ? 5 : 4} class="section">${s.title}</td></tr>
      ${s.lines.map(
        (l) => html`
          <tr>
            <td>${l.description} <span class="kind">${l.kind === 'manual' ? 'manual' : l.kind === 'remaining' ? 'guaranteed adjustment' : ''}</span></td>
            <td class="num">${l.units}</td>
            <td class="num">${l.unit_price}</td>
            <td class="num">${l.amount}</td>
            ${draft
              ? html`<td class="actions">
                  <wa-button size="small" appearance="plain" @click=${() => this.openLine(l)}>Edit</wa-button>
                  <wa-button size="small" appearance="plain" variant="danger" @click=${() => this.removeLine(l)}>Remove</wa-button>
                </td>`
              : nothing}
          </tr>
        `,
      )}
      <tr class="subtotal"><td class="num">Subtotal</td><td class="num">${s.units}</td><td></td><td class="num">${s.subtotal}</td>${draft ? html`<td></td>` : nothing}</tr>
    `;
  }

  private renderLineDialog() {
    const l = this.lineEditing;
    const d = this.detail;
    return html`
      <wa-dialog label=${l ? 'Edit line' : 'Add line'} ?open=${this.lineDialogOpen} @wa-after-hide=${this.onDialogHidden} style="--width: min(34rem, calc(100vw - 1.5rem));">
        <form id="line-form" @submit=${this.saveLine}>
          ${!l
            ? html`
                <wa-select class="full" name="section_id" label="Section" .value=${d?.sections[0] ? String(d.sections[0].id) : '__new__'} hint="Pick an existing section or start a new one">
                  ${(d?.sections ?? []).map((s) => html`<wa-option value=${String(s.id)}>${s.title}</wa-option>`)}
                  <wa-option value="__new__">New section…</wa-option>
                </wa-select>
                <wa-input class="full" name="section_title" label="New section title" placeholder="Bank fees"></wa-input>
              `
            : nothing}
          <wa-input class="full" name="description" label="Description" required .value=${l?.description ?? ''}></wa-input>
          <wa-input name="units" label="Units" inputmode="decimal" required .value=${l?.units ?? '1'} hint="Hours or pieces; negative allowed"></wa-input>
          <wa-input name="unit_price" label="Unit price" inputmode="decimal" required .value=${l?.unit_price ?? ''}></wa-input>
          ${l?.kind === 'generated' || l?.kind === 'remaining' ? html`<p class="muted full">A recalculation resets generated lines to the timesheet values.</p>` : nothing}
          ${this.lineError ? html`<wa-callout class="form-error" variant="danger">${this.lineError}</wa-callout>` : nothing}
        </form>
        <div slot="footer" class="footer">
          <wa-button appearance="outlined" @click=${() => (this.lineDialogOpen = false)}>Cancel</wa-button>
          <wa-button variant="brand" type="submit" form="line-form" ?loading=${this.busy}>${l ? 'Save' : 'Add'}</wa-button>
        </div>
      </wa-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'invoice-page': InvoicePage;
  }
}
